import { ChevronDown, Check, X as XIcon, CheckCircle2 } from "lucide-react";
import { useState } from "react";

export function Section({
  title,
  children,
  defaultOpen = true,
  accent = false,
  complete,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  accent?: boolean;
  // When set, the header turns light green (with a check) once the section's
  // fields are all filled, so the user can see it's done at a glance.
  complete?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const headerCls = complete
    ? "bg-emerald-100 hover:bg-emerald-200 text-emerald-900"
    : accent
      ? "bg-[var(--hmg-red)] text-white"
      : "bg-slate-50 hover:bg-slate-100 text-slate-900";
  return (
    <section
      className={`bg-white rounded-xl border overflow-hidden ${complete ? "border-emerald-200" : "border-slate-200"}`}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between px-4 py-3 text-left ${headerCls}`}
      >
        <h3 className="font-semibold inline-flex items-center gap-2">
          {complete && (
            <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          )}
          {title}
        </h3>
        <ChevronDown
          className={`w-5 h-5 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="p-4">{children}</div>}
    </section>
  );
}

export function CheckRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 py-1.5 px-2 rounded hover:bg-slate-50 cursor-pointer text-sm">
      <span className="text-slate-700 flex-1">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative w-14 h-7 rounded-full transition-colors flex-shrink-0 ${checked ? "bg-emerald-500" : "bg-slate-300"}`}
        aria-label={`${label}: ${checked ? "Available" : "Not available"}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white flex items-center justify-center transition-transform shadow-sm ${checked ? "translate-x-7" : ""}`}
        >
          {checked ? (
            <Check className="w-3.5 h-3.5 text-emerald-600" />
          ) : (
            <XIcon className="w-3.5 h-3.5 text-slate-400" />
          )}
        </span>
      </button>
    </label>
  );
}

export function CheckGrid({
  items,
  values,
  onChange,
}: {
  items: ReadonlyArray<
    readonly [string, string] | readonly [string, string, string]
  >;
  values: Record<string, boolean>;
  onChange: (key: string, value: boolean) => void;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 divide-y md:divide-y-0 divide-slate-100">
      {items.map((tuple) => {
        const [key, label] = tuple;
        return (
          <CheckRow
            key={key}
            label={label}
            checked={!!values[key]}
            onChange={(v) => onChange(key, v)}
          />
        );
      })}
    </div>
  );
}

export function QtyCheckGrid({
  items,
  values,
  onChange,
}: {
  items: ReadonlyArray<readonly [string, string, string]>;
  values: Record<string, boolean>;
  onChange: (key: string, value: boolean) => void;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 divide-y md:divide-y-0 divide-slate-100">
      {items.map(([key, label, qty]) => (
        <CheckRow
          key={key}
          label={`${label}  (×${qty})`}
          checked={!!values[key]}
          onChange={(v) => onChange(key, v)}
        />
      ))}
    </div>
  );
}

export function TextField({
  label,
  value,
  onChange,
  required,
  type = "text",
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--hmg-red)]"
      />
    </div>
  );
}
